// @ts-nocheck
import React from 'react';
import type { MovementTrackMode } from '../../utils/movementPoseEngine';

type Props = {
  mode: MovementTrackMode;
  optionCount: number;
  isTrueFalse: boolean;
  /** 0..n-1 khi đang nhận diện ổn định; null = không nhấn mạnh */
  activeSlot: number | null;
  /** đang ở chế độ nhiều đáp án — chỉ hiện khung tổng quát */
  isMulti: boolean;
  /** Nhãn 2 vùng trái/phải (MCQ 2 đáp án hoặc Đúng/Sai) */
  splitLabels?: [string, string];
  /** Thời gian giữ để chốt (hiển thị chân dung) */
  holdConfirmSeconds?: number;
  /** Chỉ hiện chữ A/B/C/D trên la bàn khi đủ 4 phương án (mặc định true — tương thích cũ) */
  showAbcdLabels?: boolean;
};

/**
 * Đầu: chỉ nghiêng trái/phải (overlay 2 phía). Dáng/tay: la bàn A–D.
 */
export const CameraPoseGuides: React.FC<Props> = ({
  mode,
  optionCount,
  isTrueFalse,
  activeSlot,
  isMulti,
  splitLabels,
  holdConfirmSeconds = 3,
}) => {
  const splitLR =
    isTrueFalse ||
    (optionCount === 2 && !isMulti) ||
    (mode === 'head' && optionCount > 2 && !isMulti);

  if (isMulti) {
    return (
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4">
        <div className="max-w-[85%] rounded-2xl border-2 border-dashed border-white/50 bg-black/20 px-4 py-3 text-center">
          <p className="text-[10px] font-black uppercase tracking-wider text-white/90">Chọn nhiều đáp án</p>
          <p className="mt-1 text-[9px] text-white/70">Dùng cử chỉ từng phương án hoặc nút bên dưới — bấm «Xong câu này» để sang câu sau.</p>
        </div>
      </div>
    );
  }

  if (splitLR) {
    const leftOn = activeSlot === 0;
    const rightOn = activeSlot === 1;
    const [t0, t1] = splitLabels || (isTrueFalse ? ['Đúng', 'Sai'] : ['Trái', 'Phải']);
    const subL =
      mode === 'head' ? 'Nghiêng trái' : mode === 'hand' ? '👍' : mode === 'pose' ? 'Giang trái' : 'Trái';
    const subR =
      mode === 'head' ? 'Nghiêng phải' : mode === 'hand' ? (isTrueFalse ? '👎' : '✌️') : mode === 'pose' ? 'Giang phải' : 'Phải';
    const secLabel = holdConfirmSeconds % 1 === 0 ? String(holdConfirmSeconds) : holdConfirmSeconds.toFixed(1).replace('.', ',');
    const headMore = mode === 'head' && optionCount > 2;
    return (
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-1">
        <div className="relative aspect-square w-[min(100%,12rem)] max-h-full pb-4">
          <div className="absolute left-1/2 top-1/2 z-[1] h-[72%] w-[52%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] border-2 border-purple-400/65 bg-purple-500/10 shadow-[inset_0_0_20px_rgba(168,85,247,0.15)]" />
          <div className="absolute left-1/2 top-1/2 z-[2] h-[78%] w-[78%] -translate-x-1/2 -translate-y-1/2">
            <div className="absolute left-1/2 top-0 h-1/2 w-0 -translate-x-1/2 border-l-2 border-dashed border-red-400/85" />
            <div className="absolute bottom-0 left-1/2 h-1/2 w-0 -translate-x-1/2 border-l-2 border-dashed border-red-400/85" />
            <div className="absolute left-0 top-1/2 h-0 w-1/2 -translate-y-1/2 border-t-2 border-dashed border-red-400/85" />
            <div className="absolute right-0 top-1/2 h-0 w-1/2 -translate-y-1/2 border-t-2 border-dashed border-red-400/85" />
          </div>
          <div className="absolute left-1/2 top-1/2 z-[3] h-7 w-7 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-white/40 bg-black/35" />
          <div
            className={`absolute left-0 top-1/2 z-[4] w-[36%] -translate-y-1/2 rounded-lg border-2 border-dashed px-1 py-1.5 text-center transition-all ${
              leftOn ? 'border-teal-300 bg-teal-500/35 shadow-[0_0_12px_rgba(45,212,191,0.45)]' : 'border-orange-400/45 bg-orange-500/15'
            }`}
          >
            <span className="block text-[9px] font-black leading-tight text-white drop-shadow">{t0}</span>
            <span className="mt-0.5 block text-[7px] font-bold uppercase text-white/85">{subL}</span>
          </div>
          <div
            className={`absolute right-0 top-1/2 z-[4] w-[36%] -translate-y-1/2 rounded-lg border-2 border-dashed px-1 py-1.5 text-center transition-all ${
              rightOn ? 'border-teal-300 bg-teal-500/35 shadow-[0_0_12px_rgba(45,212,191,0.45)]' : 'border-orange-400/45 bg-orange-500/15'
            }`}
          >
            <span className="block text-[9px] font-black leading-tight text-white drop-shadow">{t1}</span>
            <span className="mt-0.5 block text-[7px] font-bold uppercase text-white/85">{subR}</span>
          </div>
          <p className="absolute bottom-0 left-0 right-0 z-[4] px-0.5 text-center text-[7px] leading-tight text-white/55">
            Chỉ nghiêng đầu · trục trái/phải — giữ ~{secLabel}s
            {headMore ? <span className="mt-0.5 block">Đáp án còn lại: chọn nút</span> : null}
          </p>
        </div>
      </div>
    );
  }

  if (mode === 'head' && !splitLR) {
    return (
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-2">
        <p className="max-w-[95%] text-center text-[9px] leading-snug text-white/70">
          Chế độ nghiêng đầu: chỉ trái/phải — chọn đáp án bằng nút.
        </p>
      </div>
    );
  }

  const n = Math.min(Math.max(optionCount, 2), 4);

  const poseCompass = [
    { slot: 0, className: 'left-[2%] top-1/2 -translate-y-1/2', letter: 'A', hint: 'T.trái' },
    { slot: 1, className: 'right-[2%] top-1/2 -translate-y-1/2', letter: 'B', hint: 'T.phải' },
    { slot: 2, className: 'left-1/2 top-[2%] -translate-x-1/2', letter: 'C', hint: 'Giơ cao' },
    { slot: 3, className: 'bottom-[2%] left-1/2 -translate-x-1/2', letter: 'D', hint: 'Khoanh' },
  ];

  const handCompass = [
    { slot: 0, className: 'left-1/2 top-[2%] -translate-x-1/2', letter: 'A', hint: '👍' },
    { slot: 1, className: 'right-[2%] top-1/2 -translate-y-1/2', letter: 'B', hint: '✌️' },
    { slot: 2, className: 'bottom-[2%] left-1/2 -translate-x-1/2', letter: 'C', hint: '🖐️' },
    { slot: 3, className: 'left-[2%] top-1/2 -translate-y-1/2', letter: 'D', hint: '☝️' },
  ];

  const compass = (mode === 'pose' ? poseCompass : handCompass).slice(0, n);
  const secLabel = holdConfirmSeconds % 1 === 0 ? String(holdConfirmSeconds) : holdConfirmSeconds.toFixed(1).replace('.', ',');

  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-1">
      <div className="relative h-[min(100%,15rem)] w-[min(100%,13rem)] max-h-full">
        <div className="absolute left-1/2 top-1/2 z-[1] h-[74%] w-[54%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] border-2 border-purple-400/70 bg-purple-500/10 shadow-[inset_0_0_24px_rgba(168,85,247,0.2)]" />
        <div className="absolute left-1/2 top-1/2 z-[2] h-[82%] w-[82%] -translate-x-1/2 -translate-y-1/2">
          <div className="absolute left-1/2 top-0 h-1/2 w-0 -translate-x-1/2 border-l-2 border-dashed border-red-400/90" />
          <div className="absolute bottom-0 left-1/2 h-1/2 w-0 -translate-x-1/2 border-l-2 border-dashed border-red-400/90" />
          <div className="absolute left-0 top-1/2 h-0 w-1/2 -translate-y-1/2 border-t-2 border-dashed border-red-400/90" />
          <div className="absolute right-0 top-1/2 h-0 w-1/2 -translate-y-1/2 border-t-2 border-dashed border-red-400/90" />
        </div>
        <div className="absolute left-1/2 top-1/2 z-[3] h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-white/45 bg-black/40" />

        {compass.map((c) => {
          const on = activeSlot === c.slot;
          return (
            <div
              key={c.slot}
              className={`absolute z-[4] flex min-w-[2.5rem] flex-col items-center justify-center rounded-lg border-2 border-dashed px-1.5 py-1 transition-all ${c.className} ${
                on ? 'border-teal-300 bg-teal-500/35 shadow-[0_0_14px_rgba(45,212,191,0.5)]' : 'border-orange-400/50 bg-orange-500/15'
              }`}
            >
              {showAbcdLabels ? (
                <>
                  <span className="text-[11px] font-black leading-none text-white drop-shadow">{c.letter}</span>
                  <span className="mt-0.5 max-w-[3.2rem] truncate text-center text-[7px] font-bold uppercase text-white/90">{c.hint}</span>
                </>
              ) : (
                <span className="max-w-[3.2rem] truncate text-center text-[8px] font-bold uppercase leading-tight text-white/95">{c.hint}</span>
              )}
            </div>
          );
        })}

        <p className="absolute bottom-0 left-1/2 z-[4] w-[115%] -translate-x-1/2 text-center text-[7px] leading-tight text-white/55">
          Lệch từ trung tâm · giữ ~{secLabel}s
        </p>
      </div>
    </div>
  );
};
